import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Box, Divider } from '@zillow/constellation-10';
import { AttachmentGuidelines } from '@zg-rentals/satellite-components';
import Layout from '../../components/Layout';
import StringConstants from '../../constants/StringConstants';
import { InboxPageContextProvider } from '../../contexts/InboxPageContextProvider';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import getValueFromEnum from '../../utils/getValueFromEnum';
import {
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import { InboxPageMobileView } from '../../../src/types/InboxPageMobileView';
import getTimezoneData from '../../utils/dateTime/getTimezoneData';
import InboxHeader from '../../components/InboxHeader';
import FiltersSelector from '../../components/FiltersSelector';
import RentalJourneyFilters from '../../components/RentalJourneyFilters';
import {
  columnWrapperStyles,
  inboxAppLayoutContainerStyles,
  inboxMainContentStyles,
  inboxPageRootStyles,
  pageContentStyles,
  primaryColumnStyles,
  rentalJourneyFiltersRowStyles,
  tertiaryColumnStyles,
} from '../../styles/inboxPageStyles';
import ConversationSearchBar from '../../components/ConversationSearchBar';
import ConversationList from '../../components/ConversationList';
import SecondaryColumn from '../../components/SecondaryColumn';
import ConversationNav from '../../components/ConversationNav';
import NotesBar from '../../components/NotesBar';
import MessageList from '../../components/MessageList';
import InboxSidebar from '../../components/InboxSidebar';
import InboxExternalLinkWarningModal from '../../components/InboxExternalLinkWarningModal';
import useDevices from '../../hooks/useDevices';
import { emitSlwInboxPageViewEvent, SLW_INBOX_PAGE_VIEW } from '../../constants/clickstreamEvents/pageView';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import { inboxPageIsLoadingStore } from '../../lib/inboxSearchListStores';
import { useSatelliteWebLandlordConversationHandler_getConversationInformationByInquiryId } from '@zg-rentals/ts-satellite-client-react-query';
import InboxPageSkeleton from '../../components/InboxPageSkeleton';
import { withRedirectBoundary } from '../../components/RedirectBoundary';
import { useAuth } from '../../AuthProvider';
import { ModalType } from '../../types/ModalType';
import useModal from '../../hooks/useModal';

export default withRedirectBoundary(
  () => {
    const { isMobileView } = useDevices();
    const { user } = useAuth();
    const router = useRouter();
    const { activeModal, clearModal } = useModal();
    const { query } = router;
    const {
      byProperty,
      conversationSlug,
      daysBack,
      folder,
      leadId,
      mobileView,
      readRepliedStatus,
      renterProgressStatus,
    } = query;
    const [listingAlias, conversationId] = conversationSlug || [];
    const { clientTimeZone, isTimeZoneForced } = getTimezoneData();
    const { updateQueryParams } = useQueryParamsNext();

    // The unifiedInquiry email will sometimes append a leadId parameter. When a leadId parameter exists,
    // locate the conversationId and redirect to the appropriate conversation
    const shouldFetchConversationInfo = !conversationId && !!leadId;

    const {
      data: conversationInfoData,
      isSuccess: isConversationInfoSuccess,
      isFetching: isFetchingConversationInfo,
    } = useSatelliteWebLandlordConversationHandler_getConversationInformationByInquiryId(
      { inquiryId: leadId?.toString() || '' },
      {
        enabled: shouldFetchConversationInfo, // Only run when leadId exists but conversationId doesn't
        meta: {
          context: {
            api: 'getConversationInformationByInquiryId',
            details: 'InboxPage - Error fetching conversation info by inquiryId',
          },
        },
      },
    );

    // Sync loading state to the shared store read across the inbox UI (search bar, sidebar, filters).
    useEffect(() => {
      if (shouldFetchConversationInfo) {
        inboxPageIsLoadingStore.set(isFetchingConversationInfo);
      }
    }, [shouldFetchConversationInfo, isFetchingConversationInfo]);

    // Handle successful fetch - redirect to the found conversation
    useEffect(() => {
      if (isConversationInfoSuccess && conversationInfoData) {
        const { conversationId, listingAlias } = conversationInfoData;

        updateQueryParams({
          queryParamValues: {
            conversationSlug: [listingAlias, conversationId],
            leadId: null,
            mobileView: InboxPageMobileView.MessageList,
          },
        });
      }
    }, [isConversationInfoSuccess, conversationInfoData, updateQueryParams]);

    useEffect(() => {
      emitSlwInboxPageViewEvent(user);
    }, []);

    const enumValuesFromQueryParams = {
      folder: getFolderFromQueryParam(folder?.toString() || ''),
      mobileView: getValueFromEnum(mobileView?.toString(), InboxPageMobileView) || InboxPageMobileView.ConversationList,
      readRepliedStatus:
        getValueFromEnum(readRepliedStatus?.toString(), SatelliteConversationReadRepliedStatusTypeV2) ||
        SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL,
      renterProgressStatus:
        getValueFromEnum(renterProgressStatus?.toString(), SatelliteConversationRenterProgressStatusTypeV2) ||
        SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
    };

    const shouldHideConversationListOnMobile =
      enumValuesFromQueryParams.mobileView != InboxPageMobileView.ConversationList;
    const shouldHideMessageListOnMobile = enumValuesFromQueryParams.mobileView != InboxPageMobileView.MessageList;
    const shouldHideRenterProfileOnMobile = enumValuesFromQueryParams.mobileView != InboxPageMobileView.RenterProfile;

    return (
      <Layout
        showFooter={false}
        isPageFluid
        mainContentStyles={inboxMainContentStyles}
        pageRootStyles={inboxPageRootStyles}
        appLayoutContainerStyles={inboxAppLayoutContainerStyles}
      >
        <Head>
          <title>{StringConstants.INBOX_PAGE_TITLE}</title>
        </Head>
        <Box css={pageContentStyles} data-eventid={SLW_INBOX_PAGE_VIEW} data-eventdescription="Page view">
          {activeModal === ModalType.AttachmentModal && <AttachmentGuidelines onClose={clearModal} />}

          <InboxExternalLinkWarningModal />

          <InboxPageContextProvider
            value={{
              clientTimeZone,
              conversationId,
              daysBack: Number(daysBack) || undefined,
              folder: enumValuesFromQueryParams.folder,
              isByProperty: !!byProperty,
              isTimeZoneForced,
              listingAlias,
              mobileView: enumValuesFromQueryParams.mobileView,
              readRepliedStatus: enumValuesFromQueryParams.readRepliedStatus,
              renterProgressStatus: enumValuesFromQueryParams.renterProgressStatus,
            }}
          >
            <>
              <InboxHeader />

              <Box
                css={{
                  ...rentalJourneyFiltersRowStyles,
                  display: shouldHideConversationListOnMobile ? { base: 'none', xxl: 'flex' } : 'flex',
                }}
              >
                <Box css={{ display: { base: 'flex', xxl: 'none' }, alignItems: 'center', flexShrink: 0 }}>
                  <FiltersSelector />
                  <Divider orientation="vertical" css={{ marginInline: 'default', height: 'token(spacing.looser)' }} />
                </Box>
                <RentalJourneyFilters />
              </Box>

              <Box css={columnWrapperStyles}>
                <Box
                  css={{
                    ...primaryColumnStyles,
                    display: shouldHideConversationListOnMobile ? { base: 'none', xxl: 'flex' } : 'flex',
                  }}
                >
                  <ConversationSearchBar />
                  <ConversationList />
                </Box>

                <SecondaryColumn shouldHideOnMobile={shouldHideMessageListOnMobile}>
                  {!!conversationId && (
                    <>
                      <ConversationNav />
                      {isMobileView && <NotesBar />}
                    </>
                  )}
                  <MessageList />
                </SecondaryColumn>

                <Box
                  css={{
                    ...tertiaryColumnStyles,
                    display: shouldHideRenterProfileOnMobile ? { base: 'none', xxl: 'flex' } : 'flex',
                  }}
                >
                  <InboxSidebar />
                </Box>
              </Box>
            </>
          </InboxPageContextProvider>
        </Box>
      </Layout>
    );
  },
  {
    loading: <InboxPageSkeleton />,
  },
);
